import { useState, useMemo } from "react";
import { NumberField, Stat, ToolShell } from "./parts";
import { inr, inrShort } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const AREAS = {
  urban: "Urban (Municipal / Corporation)",
  rural: "Rural (Panchayat)",
};

export function WbStampDuty() {
  const [value, setValue] = useState(6000000);
  const [area, setArea] = useState("urban");

  const { stampRate, stampDuty, regFee, total, effective } = useMemo(() => {
    const aboveCr = value > 10000000;
    let stampRate;
    if (area === "urban") stampRate = aboveCr ? 7 : 6;
    else stampRate = aboveCr ? 6 : 5;
    const stampDuty = (value * stampRate) / 100;
    const regFee = value * 0.01; // 1% registration fee
    const total = stampDuty + regFee;
    const effective = value ? (total / value) * 100 : 0;
    return { stampRate, stampDuty, regFee, total, effective };
  }, [value, area]);

  return (
    <ToolShell title="Stamp Duty & Registration — West Bengal" desc="Estimate the one-time stamp duty and registration charges payable in West Bengal, based on your property location and value.">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="space-y-8">
          <NumberField label="Property Market Value" value={value} onChange={setValue} min={500000} max={200000000} step={100000} prefix="₹" testid="wb-value" />
          <div className="space-y-3">
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Property Location</label>
            <Select value={area} onValueChange={setArea}>
              <SelectTrigger className="tool-input rounded-none" data-testid="wb-area"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(AREAS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="border border-border bg-background p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Applicable stamp duty rate</p>
            <p className="mt-1 font-mono text-lg text-foreground">{stampRate}% {value > 10000000 ? "(value above ₹1 Cr)" : "(value up to ₹1 Cr)"}</p>
          </div>
        </div>
        <div className="space-y-5">
          <Stat label="Total Payable" value={inr(total)} highlight testid="wb-result" />
          <div className="grid grid-cols-2 gap-3">
            <Stat label={`Stamp Duty (${stampRate}%)`} value={inrShort(stampDuty)} />
            <Stat label="Registration (1%)" value={inrShort(regFee)} />
          </div>
          <Stat label="Effective Rate" value={effective.toFixed(2) + "%"} />
          <p className="border border-border bg-background p-4 font-mono text-xs leading-relaxed text-muted-foreground">
            Charges are levied on the higher of the agreement value or the government's circle/market value. Rates are indicative for West Bengal; verify current rates on the state registration portal before payment.
          </p>
        </div>
      </div>
    </ToolShell>
  );
}
